import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { request } from '../../utils/api-client';
import { getTenantApiPath } from '../routing';
import { withTelegramAuth } from '../telegramAuth';

export interface MiniAppUser {
  id: number;
  telegramId: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  languageCode: string | null;
  isManager: boolean;
  isCourier: boolean;
}

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: MiniAppUser }
  | { status: 'unauthenticated' };

const AuthContext = createContext<AuthState>({ status: 'loading' });

export function useMiniAppAuth(): AuthState {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const resolveAuth = async () => {
      try {
        const response = await request<{ authenticated: boolean; data: MiniAppUser | null }>(
          getTenantApiPath('/web/auth/me'),
          withTelegramAuth(),
        );

        if (cancelled) {
          return;
        }

        setState(
          response.authenticated && response.data
            ? { status: 'authenticated', user: response.data }
            : { status: 'unauthenticated' },
        );
      } catch {
        if (!cancelled) {
          setState({ status: 'unauthenticated' });
        }
      }
    };

    void resolveAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  return <AuthContext value={state}>{children}</AuthContext>;
}
